import { getSignupPageArea, type SignupPageArea } from '@/utils/getSignupPageArea'
import { getAttributionContext, type AttributionContext } from '@/utils/attributionContext'

export type SignupPremiumType = 'Standard' | 'Metsikon' | 'Super' | 'Upgrade to Super'

export type SignupDestination =
  | 'Google'
  | 'Facebook'
  | 'Apple'
  | 'Email'
  | 'EmailPassword'
  | 'EmailPasswordSignup'
  | 'SSO'
  | 'Unknown'

export type PremiumSubscribedDestination = 'PaddleCheckout' | 'PaddlePlanChange'

export type PremiumDuration = 'Monthly' | 'Yearly'
export type PremiumLifecycleStage =
  | 'Prompt'
  | 'CheckoutInitiated'
  | 'TrialStarted'
  | 'PurchaseCompleted'
  | 'PlanChanged'
  | 'Error'

export interface SignupAnalyticsContext extends AttributionContext {
  Source: string
  Type: SignupPremiumType
  Page: SignupPageArea
}

export interface PremiumCheckoutAnalyticsContext extends SignupAnalyticsContext {
  Duration?: PremiumDuration
  Email?: string
  'Lifecycle Stage'?: PremiumLifecycleStage
}

type SignupDestinationInput =
  | SignupDestination
  | 'google'
  | 'facebook'
  | 'apple'
  | 'email'
  | 'emailpassword'
  | 'sso'

type AnalyticsPayload = Record<string, unknown>

function normalizeSource(value?: string | null): string {
  const normalized = value?.trim()
  return normalized && normalized.length > 0 ? normalized : 'Unknown'
}

function asPayload(data?: unknown): AnalyticsPayload {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return {}
  }
  return data as AnalyticsPayload
}

function readStringValue(
  payload: AnalyticsPayload,
  keys: readonly string[],
): string | undefined {
  for (const key of keys) {
    const value = payload[key]
    if (typeof value === 'string' && value.trim()) {
      return value
    }
  }
  return undefined
}

function buildContextFromPayload(payload: AnalyticsPayload): SignupAnalyticsContext {
  return buildSignupAnalyticsContext({
    source: readStringValue(payload, ['Source', 'source']),
    type: readStringValue(payload, ['Type', 'type']),
    page: (readStringValue(payload, ['Page', 'page']) as SignupPageArea | undefined) ?? null,
    pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
  })
}

function normalizeType(value?: string | null): SignupPremiumType {
  if (value === 'Upgrade to Super') return 'Upgrade to Super'
  if (value === 'Super' || value === 'super') return 'Super'
  return value === 'Metsikon' ? 'Metsikon' : 'Standard'
}

function normalizeDuration(value?: string | null): PremiumDuration | undefined {
  if (value === 'Monthly' || value === 'Yearly') {
    return value
  }
  return undefined
}

function normalizeDestination(value?: string | null): SignupDestination {
  switch (value) {
    case 'google':
    case 'Google':
      return 'Google'
    case 'facebook':
    case 'Facebook':
      return 'Facebook'
    case 'apple':
    case 'Apple':
      return 'Apple'
    case 'email':
    case 'Email':
      return 'Email'
    case 'emailpassword':
    case 'EmailPassword':
      return 'EmailPassword'
    case 'EmailPasswordSignup':
      return 'EmailPasswordSignup'
    case 'sso':
    case 'SSO':
      return 'SSO'
    default:
      return 'Unknown'
  }
}

export function buildSignupAnalyticsContext(params: {
  source?: string | null
  type?: SignupPremiumType | string | null
  page?: SignupPageArea | null
  pathname?: string
  search?: string
  includeAttribution?: boolean
}): SignupAnalyticsContext {
  const attribution = params.includeAttribution === false ? {} : getAttributionContext(params.search)

  return {
    Source: normalizeSource(params.source),
    Type: normalizeType(params.type),
    Page: params.page ?? getSignupPageArea(params.pathname),
    ...attribution,
  }
}

export function buildPremiumCheckoutAnalyticsContext(params: {
  source?: string | null
  type?: SignupPremiumType | string | null
  duration?: PremiumDuration | string | null
  email?: string | null
  page?: SignupPageArea | null
  pathname?: string
}): PremiumCheckoutAnalyticsContext {
  const context = buildSignupAnalyticsContext(params)
  const duration = normalizeDuration(params.duration)
  return {
    ...context,
    ...(duration ? { Duration: duration } : {}),
    ...(params.email ? { Email: params.email } : {}),
  }
}

export function buildSignUpPromptProperties(
  context: SignupAnalyticsContext,
): SignupAnalyticsContext {
  return context
}

export function buildSignUpClickProperties(params: {
  context: SignupAnalyticsContext
  destination: SignupDestinationInput | string | null | undefined
}): SignupAnalyticsContext & {
  Destination: string
  'Destination Canonical': SignupDestination
} {
  const rawDestination = params.destination?.toString().trim()
  return {
    ...params.context,
    Destination: rawDestination && rawDestination.length > 0 ? rawDestination : 'Unknown',
    'Destination Canonical': normalizeDestination(params.destination),
  }
}

export function buildSignUpSuccessProperties(params: {
  context: SignupAnalyticsContext
  destination: SignupDestinationInput | string | null | undefined
}): SignupAnalyticsContext & {
  Destination: string
  'Destination Canonical': SignupDestination
} {
  const rawDestination = params.destination?.toString().trim()
  const legacyDestination =
    rawDestination && rawDestination.length > 0
      ? rawDestination === rawDestination.toLowerCase()
        ? rawDestination.charAt(0).toUpperCase() + rawDestination.slice(1)
        : rawDestination
      : 'Unknown'
  return {
    ...params.context,
    Destination: legacyDestination,
    'Destination Canonical': normalizeDestination(params.destination),
  }
}

export function buildPremiumPromptProperties(
  context: SignupAnalyticsContext,
): SignupAnalyticsContext & {
  'Lifecycle Stage': PremiumLifecycleStage
  Plan: string
  Price: string
} {
  return {
    ...context,
    'Lifecycle Stage': 'Prompt',
    Plan: 'Unknown',
    Price: 'Unknown',
  }
}

export function buildPremiumSubscribedProperties(params: {
  context: PremiumCheckoutAnalyticsContext
  destination: PremiumSubscribedDestination
  lifecycleStage?: PremiumLifecycleStage
}): PremiumCheckoutAnalyticsContext & {
  Destination: PremiumSubscribedDestination
  'Lifecycle Stage': PremiumLifecycleStage
} {
  const lifecycleStage =
    params.lifecycleStage ??
    (params.destination === 'PaddlePlanChange' ? 'PlanChanged' : 'PurchaseCompleted')
  return {
    ...params.context,
    Destination: params.destination,
    'Lifecycle Stage': lifecycleStage,
  }
}

export function buildPremiumCheckoutStartedProperties(
  context: PremiumCheckoutAnalyticsContext,
): PremiumCheckoutAnalyticsContext & { Destination: PremiumSubscribedDestination; 'Lifecycle Stage': PremiumLifecycleStage } {
  return {
    ...context,
    Destination: 'PaddleCheckout',
    'Lifecycle Stage': 'CheckoutInitiated',
  }
}

export function normalizeSignupPremiumEventPayload(
  event: string,
  data?: unknown,
): AnalyticsPayload {
  const payload = asPayload(data)

  if (event === 'Sign up Prompt') {
    return {
      ...payload,
      ...buildSignUpPromptProperties(buildContextFromPayload(payload)),
    }
  }

  if (event === 'Sign up click' || event === 'Sign up error') {
    const destination = readStringValue(payload, ['Destination', 'destination'])
    return {
      ...payload,
      ...buildSignUpClickProperties({
        context: buildContextFromPayload(payload),
        destination,
      }),
    }
  }

  if (event === 'Sign up Success') {
    const destination = readStringValue(payload, ['Destination', 'destination'])
    return {
      ...payload,
      ...buildSignUpSuccessProperties({
        context: buildContextFromPayload(payload),
        destination,
      }),
    }
  }

  if (event === 'Premium Prompt') {
    return {
      ...payload,
      ...buildPremiumPromptProperties(buildContextFromPayload(payload)),
    }
  }

  if (event === 'Premium Subscribed' || event === 'Premium Subscribed Error') {
    const context = buildPremiumCheckoutAnalyticsContext({
      source: readStringValue(payload, ['Source', 'source']),
      type: readStringValue(payload, ['Type', 'type']),
      page: (readStringValue(payload, ['Page', 'page']) as SignupPageArea | undefined) ?? null,
      duration: readStringValue(payload, ['Duration', 'duration']),
      email: readStringValue(payload, ['Email', 'email']),
      pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
    })
    const destinationRaw = readStringValue(payload, ['Destination', 'destination'])
    const destination =
      destinationRaw === 'PaddlePlanChange' ? 'PaddlePlanChange' : 'PaddleCheckout'
    const lifecycleStageRaw = readStringValue(payload, ['Lifecycle Stage', 'lifecycle_stage'])
    const lifecycleStage =
      lifecycleStageRaw === 'TrialStarted' ||
      lifecycleStageRaw === 'PurchaseCompleted' ||
      lifecycleStageRaw === 'PlanChanged' ||
      lifecycleStageRaw === 'CheckoutInitiated' ||
      lifecycleStageRaw === 'Prompt' ||
      lifecycleStageRaw === 'Error'
        ? lifecycleStageRaw
        : event === 'Premium Subscribed Error'
          ? 'Error'
          : undefined

    return {
      ...payload,
      ...buildPremiumSubscribedProperties({
        context,
        destination,
        lifecycleStage,
      }),
    }
  }

  return payload
}
